import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ReportFilters } from '../hooks/useReports';

export interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: ReportFilters) => void;
  onClearFilters: () => void;
  currentFilters: ReportFilters;
  facets?: {
    tags?: { [key: string]: number };
    suburbs?: { [key: string]: number };
    councils?: { [key: string]: number };
  };
}

// Status options for filtering
const statusOptions = [
  { value: '', label: 'Any Status' },
  { value: 'active', label: 'Available' },
  { value: 'going_soon', label: 'Going Soon' },
  { value: 'gone', label: 'Gone' }
];

// Category options with tags
const categoryOptions = [
  { id: 'furniture', name: 'Furniture', icon: '🪑', tag: 'furniture' },
  { id: 'electronics', name: 'Electronics', icon: '📱', tag: 'electronics' },
  { id: 'books', name: 'Books', icon: '📚', tag: 'books' },
  { id: 'clothing', name: 'Clothing', icon: '👕', tag: 'clothing' },
  { id: 'appliances', name: 'Appliances', icon: '🔌', tag: 'appliances' },
  { id: 'toys', name: 'Toys', icon: '🧸', tag: 'toys' },
  { id: 'garden', name: 'Garden', icon: '🌱', tag: 'garden' },
  { id: 'sports', name: 'Sports', icon: '⚽', tag: 'sports' },
  { id: 'art', name: 'Art', icon: '🎨', tag: 'art' },
  { id: 'tools', name: 'Tools', icon: '🔧', tag: 'tools' },
  { id: 'kitchen', name: 'Kitchen', icon: '🍳', tag: 'kitchen' }
];

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  onClearFilters,
  currentFilters,
  facets
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const [localFilters, setLocalFilters] = useState<ReportFilters>(currentFilters);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragCurrentY, setDragCurrentY] = useState(0);

  // Update local filters when current filters change
  useEffect(() => {
    setLocalFilters(currentFilters);
  }, [currentFilters]);

  // Handle backdrop click to close
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    // Close if clicking on the backdrop or the outer container
    if (e.target === backdropRef.current || e.currentTarget === backdropRef.current) {
      onClose();
    }
  }, [onClose]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'Escape':
        onClose();
        break;
      case 'Tab':
        // Trap focus within the drawer
        const focusableElements = drawerRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements && focusableElements.length > 0) {
          const firstElement = focusableElements[0] as HTMLElement;
          const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
          
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
        break;
    }
  }, [isOpen, onClose]);

  // Handle keyboard events and focus management
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = 'hidden';
      
      // Focus the first focusable element when drawer opens
      setTimeout(() => {
        const firstFocusable = drawerRef.current?.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;
        if (firstFocusable) {
          firstFocusable.focus();
        }
      }, 100);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  // Touch/swipe handling for mobile UX
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setIsDragging(true);
    setDragStartY(e.touches[0].clientY);
    setDragCurrentY(e.touches[0].clientY);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    
    const currentY = e.touches[0].clientY;
    setDragCurrentY(currentY);
    
    // Only allow dragging down
    const deltaY = Math.max(0, currentY - dragStartY);
    
    if (drawerRef.current) {
      drawerRef.current.style.transform = `translateY(${deltaY}px)`;
    }
  }, [isDragging, dragStartY]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    const deltaY = dragCurrentY - dragStartY;
    
    // If dragged down more than 100px, close the drawer
    if (deltaY > 100) {
      onClose();
    } else {
      // Snap back to original position
      if (drawerRef.current) {
        drawerRef.current.style.transform = 'translateY(0)';
      }
    }
  }, [isDragging, dragCurrentY, dragStartY, onClose]);

  // Handle filter changes
  const handleStatusChange = useCallback((status: string) => {
    setLocalFilters(prev => ({ ...prev, status: status || undefined }));
  }, []);

  const handleTagToggle = useCallback((tag: string) => {
    setLocalFilters(prev => {
      const currentTags = prev.tags || [];
      const newTags = currentTags.includes(tag)
        ? currentTags.filter(t => t !== tag)
        : [...currentTags, tag];
      
      return { ...prev, tags: newTags.length > 0 ? newTags : undefined };
    });
  }, []);

  const handleCategoryToggle = useCallback((category: string) => {
    setLocalFilters(prev => {
      const currentCategories = prev.categories || [];
      const newCategories = currentCategories.includes(category)
        ? currentCategories.filter(c => c !== category)
        : [...currentCategories, category];
      
      return { ...prev, categories: newCategories.length > 0 ? newCategories : undefined };
    });
  }, []);

  const handleDateFromChange = useCallback((date: string) => {
    setLocalFilters(prev => ({
      ...prev,
      dateFrom: date ? new Date(date) : undefined
    }));
  }, []);

  const handleDateToChange = useCallback((date: string) => {
    setLocalFilters(prev => ({
      ...prev,
      dateTo: date ? new Date(date) : undefined
    }));
  }, []);

  const handleApply = useCallback(() => {
    onApplyFilters(localFilters);
    onClose();
  }, [localFilters, onApplyFilters, onClose]);

  const handleClear = useCallback(() => {
    const clearedFilters: ReportFilters = {
      sortBy: 'submittedAt',
      sortOrder: 'desc',
      limit: 20
    };
    setLocalFilters(clearedFilters);
    onClearFilters();
    onClose();
  }, [onClearFilters, onClose]);

  // Get popular filters from facets
  const getPopularFilters = useCallback(() => {
    if (!facets?.tags) return [];
    
    return Object.entries(facets.tags)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 6)
      .map(([tag, count]) => ({ tag, count }));
  }, [facets]);

  if (!isOpen) return null;

  return (
    <div
      ref={backdropRef}
      className={`fixed inset-0 z-50 flex items-end justify-center transition-all duration-300 ease-out ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="filter-drawer-title"
    >
      {/* Backdrop with fade animation */}
      <div className={`absolute inset-0 bg-black transition-opacity duration-300 ease-out ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      }`} />
      
      {/* Drawer with slide animation */}
      <div
        ref={drawerRef}
        className={`relative bg-white rounded-t-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        role="dialog"
        aria-labelledby="filter-drawer-title"
      >
        {/* Drag Handle */}
        <div className="flex justify-center py-2" aria-hidden="true">
          <div className="w-12 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 id="filter-drawer-title" className="text-lg font-semibold text-urban">
            Filters
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center focus:ring-2 focus:ring-cardboard focus:ring-offset-2"
            aria-label="Close filters"
          >
            <svg 
              className="w-5 h-5 text-gray-500" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1">
          {/* Popular Filters Section */}
          {getPopularFilters().length > 0 && (
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-urban mb-3">Popular Filters</h3>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Popular filter options">
                {getPopularFilters().map(({ tag, count }) => {
                  const isSelected = localFilters.tags?.includes(tag);
                  return (
                    <button
                      key={tag}
                      onClick={() => handleTagToggle(tag)}
                      className={`px-3 py-2 rounded-full text-sm font-medium transition-colors min-h-[44px] focus:ring-2 focus:ring-cardboard focus:ring-offset-2 ${
                        isSelected
                          ? 'bg-cardboard text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      aria-pressed={isSelected}
                      aria-label={`${isSelected ? 'Remove' : 'Add'} ${tag} filter (${count} items)`}
                    >
                      {tag} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Status Section */}
          <fieldset className="px-6 py-4 border-b border-gray-200">
            <legend className="text-sm font-semibold text-urban mb-3">Status</legend>
            <div className="space-y-2" role="radiogroup" aria-label="Item status filter">
              {statusOptions.map(option => (
                <label key={option.value} className="flex items-center min-h-[44px]">
                  <input
                    type="radio"
                    name="status"
                    value={option.value}
                    checked={(localFilters.status || '') === option.value}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="mr-3 text-cardboard focus:ring-cardboard focus:ring-2"
                    aria-describedby={`status-${option.value}-desc`}
                  />
                  <span 
                    id={`status-${option.value}-desc`}
                    className="text-sm text-gray-700"
                  >
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* Categories Section */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-urban mb-3">Categories</h3>
            <div className="grid grid-cols-2 gap-2" role="group" aria-label="Category filter options">
              {categoryOptions.map(category => {
                const isSelected = localFilters.categories?.includes(category.name);
                return (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryToggle(category.name)}
                    className={`flex items-center p-3 rounded-lg text-left transition-colors min-h-[44px] focus:ring-2 focus:ring-cardboard focus:ring-offset-2 ${
                      isSelected
                        ? 'bg-cardboard text-white'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                    aria-pressed={isSelected}
                    aria-label={`${isSelected ? 'Remove' : 'Add'} ${category.name} category filter`}
                  >
                    <span className="text-lg mr-2" aria-hidden="true">{category.icon}</span>
                    <span className="text-sm font-medium">{category.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date Range Section */}
          <fieldset className="px-6 py-4">
            <legend className="text-sm font-semibold text-urban mb-3">Date Range</legend>
            <div className="space-y-3">
              <div>
                <label htmlFor="date-from" className="block text-xs text-gray-600 mb-1">From</label>
                <input
                  id="date-from"
                  type="date"
                  value={localFilters.dateFrom ? localFilters.dateFrom.toISOString().split('T')[0] : ''}
                  onChange={(e) => handleDateFromChange(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cardboard focus:border-cardboard min-h-[44px]"
                  aria-describedby="date-from-desc"
                />
                <div id="date-from-desc" className="sr-only">
                  Select the earliest date to filter items from
                </div>
              </div>
              <div>
                <label htmlFor="date-to" className="block text-xs text-gray-600 mb-1">To</label>
                <input
                  id="date-to"
                  type="date"
                  value={localFilters.dateTo ? localFilters.dateTo.toISOString().split('T')[0] : ''}
                  onChange={(e) => handleDateToChange(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cardboard focus:border-cardboard min-h-[44px]"
                  aria-describedby="date-to-desc"
                />
                <div id="date-to-desc" className="sr-only">
                  Select the latest date to filter items to
                </div>
              </div>
            </div>
          </fieldset>
        </div>

        {/* Action Buttons */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex gap-3">
            <button
              onClick={handleClear}
              className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors min-h-[44px] focus:ring-2 focus:ring-cardboard focus:ring-offset-2"
              aria-label="Clear all active filters"
            >
              Clear All
            </button>
            <button
              onClick={handleApply}
              className="flex-1 py-3 px-4 bg-cardboard hover:bg-cardboard-600 text-white rounded-lg text-sm font-medium transition-colors min-h-[44px] focus:ring-2 focus:ring-cardboard focus:ring-offset-2"
              aria-label="Apply selected filters to search results"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterDrawer;